import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { useOrganization } from '_/contexts/OrganizationContext';
import { IFlow } from '_/features/flows/common/types/flow.types';
import { useCancelDelivery } from '_/hooks/useDeliveries';
import useProjectLockAccess from '_/hooks/useProjectLockAccess';
import { IDelivery } from '_/types/deliveries/delivery.types';
import { formatDateTime } from '_/utils/dateUtils';
import { AlarmState } from '_/utils/deliveryUtils';
import { Typography, Tag, Button, Col, Modal, Space } from 'antd';
import type { PresetColorType, PresetStatusColorType } from 'antd/es/_util/colors';
import queryString from 'query-string';
import React, { useState, Fragment, ReactElement } from 'react';

const getAlarmStateTagColor = (alarmState?: AlarmState | string): PresetColorType | PresetStatusColorType => {
  switch (alarmState) {
    case 'OK':
      return 'success';
    case 'NO_DATA':
      return 'default';
    case 'INFO':
      return 'blue';
    case 'WARNING':
      return 'warning';
    case 'ERROR':
    case 'CRITICAL':
      return 'error';
    default:
      return 'default';
  }
};

interface DeliveryInfoCardProps {
  delivery?: IDelivery;
  showDrilldownButton: boolean;
  selectedFlow?: IFlow | null;
}

function DeliveryInfoCard({ delivery, showDrilldownButton, selectedFlow }: DeliveryInfoCardProps): ReactElement {
  const access = useProjectLockAccess();
  const history = useNavigate();
  const location = useLocation();
  const { selectedOrgSlug } = useOrganization();

  // Parse publicToken from query string
  const searchString = typeof location.search === 'object' ? '' : (location.search as string) || '';
  const queryParams = queryString.parse(searchString);
  const publicToken = queryParams.token as string | undefined;

  // Determine if we're on org deliveries page (no 'flows' in the path)
  const orgDeliveries = !location.pathname.includes('/flows/');

  const [isCancelDeliveryModalOpen, setIsCancelDeliveryModalOpen] = useState(false);

  // React Query hook
  const cancelDeliveryMutation = useCancelDelivery();

  const flowSlug = delivery?.flow?.slug || selectedFlow?.slug;
  const projectSlug = delivery?.flow?.project?.slug || selectedFlow?.project?.slug;

  const handleClickDrilldown = async () => {
    if (!delivery) return;

    const tokenParameter = publicToken ? `?token=${publicToken}` : '';
    const path = `/orgs/${selectedOrgSlug}/projects/${projectSlug}/flows/${flowSlug}/deliveries/${delivery.id}${tokenParameter}`;

    void history({ to: `${publicToken ? '/public' : ''}${path}` });
  };

  const formatAlarmLink = () => {
    if (!delivery) return '';

    const tokenParameter = publicToken ? `?token=${publicToken}` : '';
    const prefix = orgDeliveries ? '' : `/projects/${projectSlug}/flows/${flowSlug}`;

    return `/orgs/${selectedOrgSlug}${prefix}/deliveries/${delivery.id}/alarms${tokenParameter}`;
  };

  const toggleDelivery = () => {
    setIsCancelDeliveryModalOpen(!isCancelDeliveryModalOpen);
  };

  const handleCancelDeliveryModalToggle = () => {
    toggleDelivery();
  };

  const handleConfirmCancel = async () => {
    if (!delivery) return;

    cancelDeliveryMutation.mutate(delivery.id, {
      onSuccess: () => {
        toggleDelivery();
      },
    });
  };

  const showCancelButton =
    delivery?.status === 'OPEN' && delivery?.type === 'SIMPLE' && !window.location.pathname.includes('/public');
  const alarmText = delivery?.alarmState || '';
  const alarmStateColor = getAlarmStateTagColor(delivery?.alarmState);

  return (
    <Fragment>
      <Col style={{ display: 'flex', flexDirection: 'column', marginBottom: 5, flexShrink: 1 }}>
        <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
          Alarm State
        </Typography.Text>
        <div style={{ marginTop: 4 }}>
          {publicToken ? (
            <Tag color={alarmStateColor}>{alarmText}</Tag>
          ) : (
            <Link to={formatAlarmLink()}>
              <Tag color={alarmStateColor}>{alarmText}</Tag>
            </Link>
          )}
        </div>
      </Col>
      <Col style={{ display: 'flex', flexDirection: 'column', marginBottom: 5, flexShrink: 1 }}>
        <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
          Started
        </Typography.Text>
        <Typography.Text>{formatDateTime(delivery?.started) || ''}</Typography.Text>
      </Col>
      <Col style={{ display: 'flex', flexDirection: 'column', marginBottom: 5, flexShrink: 1 }}>
        <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
          Collect Data By
        </Typography.Text>
        <Typography.Text>{formatDateTime(delivery?.collectBy) || ''}</Typography.Text>
      </Col>
      <Col style={{ display: 'flex', flexDirection: 'column', marginBottom: 5, flexShrink: 1 }}>
        <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
          Deliver Data By
        </Typography.Text>
        <Typography.Text>{formatDateTime(delivery?.deliverBy) || ''}</Typography.Text>
      </Col>
      <Col style={{ display: 'flex', flexDirection: 'column', marginBottom: 5, flexShrink: 1 }}>
        <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
          Close By
        </Typography.Text>
        <Typography.Text>{formatDateTime(delivery?.endBy) || ''}</Typography.Text>
      </Col>
      <Col style={{ display: 'flex', flexDirection: 'column', marginBottom: 5, flexShrink: 1 }}>
        <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
          ID
        </Typography.Text>
        <Typography.Text style={{ maxWidth: '89%' }}>{delivery?.id || ''}</Typography.Text>
      </Col>
      <Space style={{ marginTop: 10 }}>
        {showDrilldownButton && (
          <Button size="small" type="default" onClick={handleClickDrilldown}>
            Drill Down
          </Button>
        )}
        {showCancelButton && (
          <Button danger type="default" size="small" disabled={!access} onClick={handleCancelDeliveryModalToggle}>
            Cancel
          </Button>
        )}
      </Space>
      <Modal
        open={isCancelDeliveryModalOpen}
        title="Cancel Delivery"
        onCancel={handleCancelDeliveryModalToggle}
        onOk={handleConfirmCancel}
        okButtonProps={{
          danger: true,
        }}
        okText="Cancel delivery"
        cancelText="Close"
      >
        <p>Are you sure that you want to cancel this delivery?</p>
      </Modal>
    </Fragment>
  );
}

export default DeliveryInfoCard;
